import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  patientInfoService,
  type DirectCorrectionAmendmentBody,
  type DirectCorrectionAmendmentResponse,
  type PatientInfoUpdateBody,
} from "../services/patientInfoService";

function getStaleLabelDescription(
  staleMaterialTypes: Array<"cassettes" | "slides">
): string {
  if (staleMaterialTypes.length === 0) {
    return "The server did not identify any stale cassette or slide labels.";
  }
  return staleMaterialTypes
    .map((materialType) =>
      materialType === "cassettes" ? "cassette labels" : "slide labels"
    )
    .join(" and ");
}

function showIdentityChangeWarning(
  response: DirectCorrectionAmendmentResponse
): void {
  const identityChange = response.identityChange;
  if (!identityChange) return;
  const staleLabels = getStaleLabelDescription(
    identityChange.staleMaterialTypes
  );
  const hasStaleLabels = identityChange.staleMaterialTypes.length > 0;
  notifications.show({
    title: "Case number changed — printed labels need attention",
    message: `${identityChange.previousIdentity.caseNumber} is now ${
      identityChange.currentIdentity.caseNumber
    }. ${hasStaleLabels ? `Reprint ${staleLabels}.` : staleLabels}`,
    color: "orange",
    autoClose: false,
  });
}

export function useUpdatePatientInfo(caseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: PatientInfoUpdateBody) =>
      patientInfoService.updatePatientInfo(caseId, body),
    onSuccess: async (data: any) => {
      const refreshes = [
        queryClient.refetchQueries({
          queryKey: ["case", caseId],
          exact: true,
          type: "active",
        }),
        queryClient.invalidateQueries({ queryKey: ["cases"] }),
        queryClient.invalidateQueries({
          queryKey: ["case-status-history", caseId],
        }),
      ];
      if (data.correctionRequestId) {
        refreshes.push(
          queryClient.invalidateQueries({
            queryKey: ["correction-requests", caseId],
          })
        );
        // Post-signoff correction request
        notifications.show({
          title: "Correction Request Submitted",
          message: "A pathologist will review your request.",
          color: "blue",
        });
      } else {
        // Pre-signoff direct update
        notifications.show({
          title: "Patient Info Updated",
          message: "Changes saved successfully.",
          color: "green",
        });
      }
      await Promise.all(refreshes);
    },
    onError: (error: any) => {
      const fieldErrors = error.response?.data?.fieldErrors;
      if (!fieldErrors) {
        notifications.show({
          title: "Error",
          message: error.message || "Failed to update patient information",
          color: "red",
        });
      }
      // If fieldErrors exist, the modal handles displaying them inline
    },
  });
}

export function usePatientInfoHistory(caseId: string | undefined) {
  return useQuery({
    queryKey: ["patient-info-history", caseId],
    queryFn: () => patientInfoService.getPatientInfoHistory(caseId!),
    enabled: !!caseId,
  });
}

export function useDirectCorrectionAmendment(caseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: DirectCorrectionAmendmentBody) =>
      patientInfoService.applyDirectCorrectionAmendment(caseId, body),
    onSuccess: async (response) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["case", caseId] }),
        queryClient.invalidateQueries({ queryKey: ["cases"] }),
        queryClient.invalidateQueries({
          queryKey: ["sign-off-history", caseId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["signed-correction-prefix-context", caseId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["case-for-pathologist-review", caseId],
        }),
        queryClient.invalidateQueries({ queryKey: ["document-view", caseId] }),
        queryClient.invalidateQueries({ queryKey: ["report-preview", caseId] }),
        queryClient.invalidateQueries({
          queryKey: ["report-timezone-correction-eligibility", caseId],
        }),
      ]);
      notifications.show({
        title: "Correction Applied",
        message: "A corrected report has been generated.",
        color: "green",
      });
      showIdentityChangeWarning(response);
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Correction could not be applied",
        message: error.message,
        color: "red",
      });
    },
  });
}

export function useDirectCorrectionPreview(caseId: string) {
  return useMutation({
    mutationFn: (body: DirectCorrectionAmendmentBody) =>
      patientInfoService.getDirectCorrectionPreview(caseId, body),
  });
}

export function useSignedCorrectionPrefixContext(
  caseId: string,
  enabled: boolean
) {
  return useQuery({
    queryKey: ["signed-correction-prefix-context", caseId],
    queryFn: () => patientInfoService.getSignedCorrectionPrefixContext(caseId),
    enabled: enabled && Boolean(caseId),
  });
}
